import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Bell, Mail, MessageSquare, Search } from "lucide-react";
import { getNotificationDeliveryLog } from "@/api/notifications";
import { Badge } from "@/components/ui/Badge";
import type { BadgeVariant } from "@/components/ui/Badge";
import { EmptyState } from "@/components/ui/EmptyState";
import { Pagination } from "@/components/ui/Pagination";
import { cn } from "@/lib/cn";
import { formatDateTime } from "@/lib/format";
import { AnalyticsFilterBar } from "@/features/reports/components/AnalyticsFilterBar";
import type { AnalyticsFilterValues } from "@/features/reports/components/AnalyticsFilterBar";
import type { NotificationsValues } from "@/features/reports/analyticsFilters";
import { useTenantTimezone } from "@/features/settings/hooks/useTenantTimezone";
import type {
  NotificationLogParams,
  NotificationLogRow,
} from "@/types/notifications";

const PAGE_SIZE = 20;
const EM_DASH = "—";

/** Inner channel sub-tabs (set the API `channel` filter). */
const CHANNELS = ["email", "sms"] as const;
type ChannelTab = (typeof CHANNELS)[number];

/** Status filter options shown in the select. */
const STATUS_OPTIONS = ["sent", "failed", "pending", "skipped"] as const;

/**
 * Known notification-type enum values (mirrors the backend
 * `Notification.NotificationType` TextChoices). Used to map a raw type to a
 * localized label key; unknown/future types fall back to a humanized form so a
 * raw enum string never leaks into the UI (TASK-3840).
 */
const KNOWN_NOTIFICATION_TYPES = new Set([
  "order_completed",
  "order_rejected",
  "time_before_arrival",
  "orders_ready_to_dispatch",
  "order_on_its_way",
  "distance_to_point",
  "tracking_link",
  "route_created",
  "route_cancelled",
  "contact_info_changed",
  "files_attached",
  "files_deleted",
  "order_params_changed",
  "delivery_time_exceeded",
  "service_time_exceeded",
  "route_deviation",
  "order_skipped",
  "no_status_set",
  "route_changed",
  "order_visited",
  "chat_message",
  "routes_built",
  "driver_account_deletion_requested",
]);

/**
 * Humanizes an unmapped enum value ("some_future_type" → "Some future type") so
 * the column never shows a raw snake_case token even for types added on the
 * backend after this map was written.
 */
function humanizeType(value: string): string {
  if (!value) return value;
  const spaced = value.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Resolves a notification_type enum to a localized human label. */
function notificationTypeLabel(
  value: string,
  t: (key: string) => string,
): string {
  if (KNOWN_NOTIFICATION_TYPES.has(value)) {
    return t(`analytics.notif.typeLabels.${value}`);
  }
  return humanizeType(value);
}

/** Maps a delivery status to a Badge variant + label key. */
function statusBadge(status: string): { variant: BadgeVariant; key: string } {
  switch (status) {
    case "sent":
      return { variant: "completed", key: "analytics.notif.statusSent" };
    case "failed":
      return { variant: "failed", key: "analytics.notif.statusFailed" };
    // BUG-543: never fall through to "pending" — a skipped row is finished, not
    // queued, and showing it as pending hides that it reached nobody.
    case "skipped":
      return { variant: "failed", key: "analytics.notif.statusSkipped" };
    default:
      return { variant: "pending", key: "analytics.notif.statusPending" };
  }
}

interface Props {
  /** Current resolved values for the namespaced notifications filter params. */
  values: NotificationsValues;
  /** Merge a patch of notifications filter params into the shared URL state. */
  setValues: (patch: Partial<NotificationsValues>) => void;
}

/**
 * Notifications delivery-log tab (Sprint 117, TASK-3831).
 *
 * Email/SMS sub-tabs drive the `channel` filter; a search box, status filter,
 * date range, and an "undelivered only" toggle refine the paginated delivery
 * log fed by `GET /api/v1/notifications/delivery-log/` (TASK-3822). Order
 * context (№ order / customer / address) renders an em-dash when absent, as
 * historical notifications carry no related order.
 *
 * The tab reads/writes the shell's namespaced URL params (`channel`, `status`,
 * `undelivered`, `n_search`, `n_page`); the date range is local component state
 * since the shell allocates no URL slot for it.
 */
export function NotificationsLogTab({ values, setValues }: Props) {
  const { t } = useTranslation();

  // Email/SMS sub-tab; default to Email when no channel is set.
  const activeChannel: ChannelTab = values.channel === "sms" ? "sms" : "email";
  const page = Number(values.n_page) || 1;
  const undelivered = values.undelivered === "true";

  // Date range + active preset — local, not URL-persisted (no namespaced slot in
  // the shell). Driven by the shared AnalyticsFilterBar so the date inputs match
  // the rest of the section (localized, no US mm/dd/yyyy bug).
  /* TASK-5041: the zone the shared bar's preset pills resolve their ranges in.
     Safe to read here — this component already runs `useQuery`, so it is always
     inside a `QueryClientProvider`. The bar itself takes the zone as a PROP
     rather than calling the hook, so it stays renderable without one. */
  const tenantTz = useTenantTimezone();

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [preset, setPreset] = useState("");

  const queryParams = useMemo<NotificationLogParams>(() => {
    const params: NotificationLogParams = {
      channel: activeChannel,
      page,
    };
    if (values.status) params.status = values.status;
    if (values.n_search) params.search = values.n_search;
    if (undelivered) params.undelivered_only = true;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return params;
  }, [
    activeChannel,
    page,
    values.status,
    values.n_search,
    undelivered,
    dateFrom,
    dateTo,
  ]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["notification-delivery-log", queryParams],
    queryFn: () => getNotificationDeliveryLog(queryParams),
    placeholderData: keepPreviousData,
  });

  const rows = data?.results ?? [];
  const total = data?.count ?? 0;
  const hasPrevious = Boolean(data?.previous);
  const hasNext = Boolean(data?.next);

  const showingLabel =
    total > 0
      ? t("analytics.notif.showing", {
          from: (page - 1) * PAGE_SIZE + 1,
          to: Math.min(page * PAGE_SIZE, total),
          total,
        })
      : "";

  const selectClass =
    "h-[30px] rounded-lg border border-gray-200 bg-gray-50 px-2.5 text-xs text-gray-900 outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary";

  // Changing the channel resets paging to page 1.
  function selectChannel(channel: ChannelTab) {
    setValues({ channel, n_page: "1" });
  }

  // Shared-bar values — Notifications has no vehicle/driver filter; only the
  // date range + preset are used, kept in local state above.
  const barValues: AnalyticsFilterValues = {
    date_from: dateFrom,
    date_to: dateTo,
    vehicle_id: "",
    driver_id: "",
    preset,
  };

  // Date range / preset live-apply through local state; paging resets to 1.
  function handleBarChange(patch: Partial<AnalyticsFilterValues>) {
    if (patch.date_from !== undefined) setDateFrom(patch.date_from);
    if (patch.date_to !== undefined) setDateTo(patch.date_to);
    if (patch.preset !== undefined) setPreset(patch.preset);
    setValues({ n_page: "1" });
  }

  return (
    <div className="space-y-4">
      {/* Filters — shared bar with channel sub-tabs (leading) and status/search/
          undelivered controls (trailing) so the date inputs match the rest of
          the analytics section (localized, no US mm/dd/yyyy bug). */}
      <AnalyticsFilterBar
        values={barValues}
        vehicleOptions={[]}
        driverOptions={[]}
        onChange={handleBarChange}
        hideVehicle
        hideDriver
        tenantTz={tenantTz}
        leadingSlot={
          <div className="inline-flex self-end rounded-xl border border-gray-200 bg-gray-50 p-1">
            {CHANNELS.map((ch) => {
              const Icon = ch === "email" ? Mail : MessageSquare;
              const active = activeChannel === ch;
              return (
                <button
                  key={ch}
                  type="button"
                  onClick={() => selectChannel(ch)}
                  aria-pressed={active}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors",
                    active
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-700",
                  )}
                >
                  <Icon size={13} className="shrink-0" />
                  {t(`analytics.notif.${ch}`)}
                </button>
              );
            })}
          </div>
        }
        rightSlot={
          <div className="flex flex-wrap items-end gap-3">
            {/* Search */}
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                {t("analytics.notif.recipient")}
              </label>
              <div className="flex min-w-[200px] items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5">
                <Search size={13} className="shrink-0 text-gray-400" />
                <input
                  type="search"
                  value={values.n_search}
                  onChange={(e) =>
                    setValues({ n_search: e.target.value, n_page: "1" })
                  }
                  placeholder={t("analytics.notif.search")}
                  aria-label={t("analytics.notif.search")}
                  className="w-full bg-transparent text-xs text-gray-900 outline-none"
                />
              </div>
            </div>

            {/* Status filter */}
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                {t("analytics.notif.status")}
              </label>
              <select
                value={values.status}
                onChange={(e) =>
                  setValues({ status: e.target.value, n_page: "1" })
                }
                aria-label={t("analytics.notif.status")}
                className={selectClass}
              >
                <option value="">{t("analytics.notif.allStatuses")}</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {t(statusBadge(s).key)}
                  </option>
                ))}
              </select>
            </div>

            {/* Undelivered toggle */}
            <label className="inline-flex h-[30px] cursor-pointer select-none items-center gap-2 text-xs font-medium text-gray-600">
              <input
                type="checkbox"
                checked={undelivered}
                onChange={(e) =>
                  setValues({
                    undelivered: e.target.checked ? "true" : "",
                    n_page: "1",
                  })
                }
                className="h-4 w-4 rounded border-gray-300 accent-brand-primary focus:ring-brand-primary"
              />
              {t("analytics.notif.undeliveredOnly")}
            </label>
          </div>
        }
      />

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[920px] text-left text-xs">
            <thead>
              <tr className="border-b border-gray-100 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                <th className="px-4 py-3">{t("analytics.notif.created")}</th>
                <th className="px-4 py-3">{t("analytics.notif.type")}</th>
                <th className="px-4 py-3">{t("analytics.notif.recipient")}</th>
                <th className="px-4 py-3">{t("analytics.notif.status")}</th>
                <th className="px-4 py-3">
                  {t("analytics.notif.undelivered")}
                </th>
                <th className="px-4 py-3 text-right">
                  {t("analytics.notif.orderNo")}
                </th>
                <th className="px-4 py-3">{t("analytics.notif.customer")}</th>
                <th className="px-4 py-3">{t("analytics.notif.address")}</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <SkeletonRows />
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4">
                    <EmptyState
                      icon={Bell}
                      title={t("analytics.notif.empty")}
                      subtitle={t("analytics.notif.emptyHint")}
                    />
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <LogRow key={row.id} row={row} tenantTz={tenantTz} />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {!isLoading && (hasPrevious || hasNext) && (
        <Pagination
          label={showingLabel}
          hasPrevious={hasPrevious && !isFetching}
          hasNext={hasNext && !isFetching}
          onPrevious={() => setValues({ n_page: String(page - 1) })}
          onNext={() => setValues({ n_page: String(page + 1) })}
        />
      )}
    </div>
  );
}

/** Single delivery-log table row. */
function LogRow({
  row,
  tenantTz,
}: {
  row: NotificationLogRow;
  tenantTz: string;
}) {
  const { t } = useTranslation();
  const badge = statusBadge(row.status);
  const recipient = row.recipient_email || row.recipient_phone || null;
  const isUndelivered = row.status === "failed" || row.status === "pending";

  return (
    <tr className="border-b border-gray-50 last:border-0 even:bg-gray-50/40 hover:bg-gray-50/60">
      <td className="whitespace-nowrap px-4 py-3 text-gray-500">
        {formatDateTime(row.created_at, { timeZone: tenantTz })}
      </td>
      <td className="px-4 py-3 text-gray-700">
        {notificationTypeLabel(row.notification_type, t)}
      </td>
      <td className="px-4 py-3 text-gray-900">
        {recipient ?? <span className="text-gray-300">{EM_DASH}</span>}
      </td>
      <td className="px-4 py-3">
        <Badge variant={badge.variant} label={t(badge.key)} />
      </td>
      <td className="px-4 py-3">
        {isUndelivered ? (
          <span
            className="inline-block h-2 w-2 rounded-full bg-brand-danger"
            aria-label={t("analytics.notif.undelivered")}
          />
        ) : (
          <span className="text-gray-300">{EM_DASH}</span>
        )}
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-gray-700">
        {row.order_external_id ?? row.order_id ?? (
          <span className="text-gray-300">{EM_DASH}</span>
        )}
      </td>
      <td className="px-4 py-3 text-gray-700">
        {row.customer_name ?? <span className="text-gray-300">{EM_DASH}</span>}
      </td>
      <td className="max-w-[260px] truncate px-4 py-3 text-gray-500">
        {row.address ?? <span className="text-gray-300">{EM_DASH}</span>}
      </td>
    </tr>
  );
}

/** Loading skeleton — a handful of shimmering placeholder rows. */
function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 6 }).map((_, i) => (
        <tr key={i} className="border-b border-gray-50">
          {Array.from({ length: 8 }).map((__, j) => (
            <td key={j} className="px-4 py-3">
              <div className="h-3 w-full animate-pulse rounded bg-gray-100" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
